/**
 * Shared helpers for the `data_table` field type (e.g. the hyreslista
 * rent-roll). Used by both the editor renderer (`fields/index.tsx`) and
 * the template Body so the column visibility + per-row computation can
 * never drift between the form and the document.
 */
import { evaluateVisibilityRule } from './visibility.js';
/** Normalise an unknown field value into `DataTableRow[]`. */
export function asDataTableRows(value) {
    if (!Array.isArray(value))
        return [];
    return value.map((row) => {
        const r = (row ?? {});
        const out = {};
        for (const [k, v] of Object.entries(r)) {
            out[k] =
                typeof v === 'number' || typeof v === 'string' || v === null
                    ? v
                    : String(v ?? '');
        }
        return out;
    });
}
/** Columns currently visible for the given contract values (variant gating). */
export function visibleColumns(columns, values) {
    return columns.filter((c) => evaluateVisibilityRule(c.visibleWhen, values));
}
/** Coerce a cell to a number for computation (handles "1 234,5" etc.). */
function toNum(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
        const cleaned = v.replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, '');
        if (cleaned === '' || cleaned === '-')
            return null;
        const n = Number.parseFloat(cleaned);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}
/**
 * The effective value of a cell. For a `computed` column it's derived from
 * the row (Phase 1: sum of `inputs`); otherwise the stored value. Returns
 * null when a computed column's inputs are all empty.
 */
export function cellValue(col, row) {
    if (col.computed?.op === 'sum') {
        const nums = col.computed.inputs.map((k) => toNum(row[k]));
        if (nums.every((n) => n == null))
            return null;
        return nums.reduce((acc, n) => acc + (n ?? 0), 0);
    }
    return row[col.key] ?? null;
}
/** Display string for a cell, formatting number/currency with sv-SE. */
export function formatCell(col, value) {
    if (value == null || value === '')
        return '';
    if (col.kind === 'currency' || col.kind === 'number') {
        const n = toNum(value);
        return n == null ? String(value) : n.toLocaleString('sv-SE');
    }
    return String(value);
}
export { toNum as dataTableToNumber };
//# sourceMappingURL=data-table.js.map